import axios from 'axios';
import {
  ComponentDefinition,
  CreateComponentRequest,
  UpdateComponentRequest
} from '../types/components';
import { Logger } from '../utils/logger';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const USE_MOCK_DATA = import.meta.env.VITE_ENVIRONMENT === 'development' && !import.meta.env.VITE_USE_REAL_API;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export class ComponentService {
  /**
   * Get all components with optional filtering
   */
  static async getComponents(params?: {
    category?: string;
    isActive?: boolean;
    tags?: string[];
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ComponentDefinition[]> {
    try {
      if (USE_MOCK_DATA) {
        // Return mock data for development
        const mockComponents: ComponentDefinition[] = [
          {
            id: '1',
            name: 'hero-banner',
            displayName: 'Hero Banner',
            description: 'A prominent banner component for landing pages',
            category: 'Content',
            version: '1.0.0',
            isActive: true,
            props: [
              {
                name: 'title',
                type: 'string',
                label: 'Banner Title',
                required: true,
                defaultValue: 'Welcome to iBusiness'
              },
              {
                name: 'subtitle',
                type: 'string',
                label: 'Subtitle',
                required: false,
                defaultValue: 'The future of lending is here'
              }
            ],
            htmlTemplate: '<div class="hero-banner"><h1>{{title}}</h1><p>{{subtitle}}</p></div>',
            cssTemplate: '.hero-banner { padding: 60px 20px; text-align: center; background: linear-gradient(27deg, #166cb1 39%, #11aae2 73%, #12d9b8); color: white; }',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: 'admin'
          }
        ];

        // Apply filters if provided
        let filtered = mockComponents;
        if (params?.category) {
          filtered = filtered.filter(c => c.category === params.category);
        }
        if (params?.isActive !== undefined) {
          filtered = filtered.filter(c => c.isActive === params.isActive);
        }
        if (params?.search) {
          const search = params.search.toLowerCase();
          filtered = filtered.filter(c => 
            c.name.toLowerCase().includes(search) ||
            c.displayName.toLowerCase().includes(search) ||
            c.description?.toLowerCase().includes(search)
          );
        }

        return filtered;
      }

      const response = await api.get('/components', { params });
      return response.data;
    } catch (error) {
      Logger.error('Failed to fetch components', error as Error, {
        service: 'ComponentService',
        operation: 'getComponents',
        params
      });
      throw error;
    }
  }

  /**
   * Get a specific component by ID
   */
  static async getComponent(id: string): Promise<ComponentDefinition> {
    try {
      if (USE_MOCK_DATA) {
        const components = await this.getComponents();
        const component = components.find(c => c.id === id);
        if (!component) {
          throw new Error('Component not found');
        }
        return component;
      }

      const response = await api.get(`/components/${id}`);
      return response.data;
    } catch (error) {
      Logger.error(`Failed to fetch component with ID: ${id}`, error as Error, {
        service: 'ComponentService',
        operation: 'getComponent',
        componentId: id
      });
      throw error;
    }
  }

  /**
   * Create a new component
   */
  static async createComponent(componentData: CreateComponentRequest): Promise<ComponentDefinition> {
    try {
      if (USE_MOCK_DATA) {
        const newComponent: ComponentDefinition = {
          id: Date.now().toString(),
          ...componentData,
          version: '1.0.0',
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'admin'
        };

        Logger.info('Component created successfully (mock)', {
          service: 'ComponentService',
          operation: 'createComponent',
          componentId: newComponent.id,
          componentName: newComponent.name
        });

        return newComponent;
      }

      const response = await api.post('/components', componentData);
      
      Logger.info('Component created successfully', {
        service: 'ComponentService',
        operation: 'createComponent',
        componentId: response.data.id,
        componentName: response.data.name
      });

      return response.data;
    } catch (error) {
      Logger.error('Failed to create component', error as Error, {
        service: 'ComponentService',
        operation: 'createComponent',
        componentData
      });
      throw error;
    }
  }

  /**
   * Update an existing component
   */
  static async updateComponent(componentData: UpdateComponentRequest): Promise<ComponentDefinition> {
    try {
      if (USE_MOCK_DATA) {
        const existingComponent = await this.getComponent(componentData.id);
        const updatedComponent: ComponentDefinition = {
          ...existingComponent,
          ...componentData,
          updatedAt: new Date().toISOString()
        };

        Logger.info('Component updated successfully (mock)', {
          service: 'ComponentService',
          operation: 'updateComponent',
          componentId: updatedComponent.id,
          componentName: updatedComponent.name
        });

        return updatedComponent;
      }

      const response = await api.put(`/components/${componentData.id}`, componentData);
      
      Logger.info('Component updated successfully', {
        service: 'ComponentService',
        operation: 'updateComponent',
        componentId: response.data.id,
        componentName: response.data.name
      });

      return response.data;
    } catch (error) {
      Logger.error(`Failed to update component with ID: ${componentData.id}`, error as Error, {
        service: 'ComponentService',
        operation: 'updateComponent',
        componentData
      });
      throw error;
    }
  }

  /**
   * Delete a component
   */
  static async deleteComponent(id: string): Promise<void> {
    try {
      if (USE_MOCK_DATA) {
        Logger.info('Component deleted successfully (mock)', {
          service: 'ComponentService',
          operation: 'deleteComponent',
          componentId: id
        });
        return;
      }

      await api.delete(`/components/${id}`);
      
      Logger.info('Component deleted successfully', {
        service: 'ComponentService',
        operation: 'deleteComponent',
        componentId: id
      });
    } catch (error) {
      Logger.error(`Failed to delete component with ID: ${id}`, error as Error, {
        service: 'ComponentService',
        operation: 'deleteComponent',
        componentId: id
      });
      throw error;
    }
  }

  /**
   * Duplicate a component
   */
  static async duplicateComponent(id: string, newName?: string): Promise<ComponentDefinition> {
    try {
      if (USE_MOCK_DATA) {
        const originalComponent = await this.getComponent(id);
        const duplicatedComponent: ComponentDefinition = {
          ...originalComponent,
          id: Date.now().toString(),
          name: newName || `${originalComponent.name}_copy`,
          displayName: newName || `${originalComponent.displayName} Copy`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        Logger.info('Component duplicated successfully (mock)', {
          service: 'ComponentService',
          operation: 'duplicateComponent',
          originalId: id,
          newId: duplicatedComponent.id,
          newName: duplicatedComponent.name
        });

        return duplicatedComponent;
      }

      const response = await api.post(`/components/${id}/duplicate`, { newName });
      
      Logger.info('Component duplicated successfully', {
        service: 'ComponentService',
        operation: 'duplicateComponent',
        originalId: id,
        newId: response.data.id,
        newName: response.data.name
      });

      return response.data;
    } catch (error) {
      Logger.error(`Failed to duplicate component with ID: ${id}`, error as Error, {
        service: 'ComponentService',
        operation: 'duplicateComponent',
        componentId: id,
        newName
      });
      throw error;
    }
  }

  /**
   * Preview a component with given props
   */
  static async previewComponent(componentId: string, props: Record<string, unknown>): Promise<{ html: string; css?: string }> {
    try {
      if (USE_MOCK_DATA) {
        const component = await this.getComponent(componentId);
        let html = component.htmlTemplate;
        
        // Replace placeholders with actual props
        Object.entries(props).forEach(([key, value]) => {
          const placeholder = `{{${key}}}`;
          html = html.replace(new RegExp(placeholder, 'g'), String(value));
        });

        return {
          html,
          css: component.cssTemplate
        };
      }

      const response = await api.post(`/components/${componentId}/preview`, { props });
      return response.data;
    } catch (error) {
      Logger.error(`Failed to preview component with ID: ${componentId}`, error as Error, {
        service: 'ComponentService',
        operation: 'previewComponent',
        componentId,
        props
      });
      throw error;
    }
  }
}